import { createContext, useContext } from 'react';
import type {
  AppRuntime,
  BootstrapContextValue,
  ContextResetController,
  RouteRestorationController,
  WorkspaceBootstrapContext,
  WorkspaceContextValue,
  WorkspaceRouteAdapter
} from './appTypes';

export const AppRuntimeContext = createContext<AppRuntime | null>(null);
export const BootstrapContext = createContext<BootstrapContextValue | null>(null);
export const WorkspaceContext = createContext<WorkspaceContextValue | null>(null);
export const WorkspaceRouteAdapterContext = createContext<WorkspaceRouteAdapter | null>(null);
export const ContextResetControllerContext = createContext<ContextResetController | null>(null);
export const RouteRestorationControllerContext = createContext<RouteRestorationController | null>(null);

export function useBootstrapContext(): BootstrapContextValue {
  const bootstrap = useContext(BootstrapContext);
  if (!bootstrap) {
    throw new Error('Member React bootstrap context is missing');
  }
  return bootstrap;
}

export function useWorkspaceBootstrapContext(): WorkspaceBootstrapContext {
  const bootstrap = useBootstrapContext();
  const appContext = bootstrap.appContext;
  if (
    !appContext?.currentUser ||
    !appContext.currentChoir ||
    !appContext.currentMembership ||
    !appContext.currentSeason
  ) {
    throw new Error('Member React workspace bootstrap context is missing');
  }
  return appContext as WorkspaceBootstrapContext;
}

export function useWorkspaceContext(): WorkspaceContextValue {
  const workspaceContext = useContext(WorkspaceContext);
  if (!workspaceContext) {
    throw new Error('Member React workspace context is missing');
  }
  return workspaceContext;
}

export function useWorkspaceRouteAdapter(): WorkspaceRouteAdapter {
  const routeAdapter = useContext(WorkspaceRouteAdapterContext);
  if (!routeAdapter) {
    throw new Error('Member React route adapter is missing');
  }
  return routeAdapter;
}

export function useRouteRestorationController(): RouteRestorationController {
  const controller = useContext(RouteRestorationControllerContext);
  if (!controller) {
    throw new Error('Member React route restoration controller is missing');
  }
  return controller;
}

export function useContextResetController(): ContextResetController {
  const resetController = useContext(ContextResetControllerContext);
  if (!resetController) {
    throw new Error('Member React context reset controller is missing');
  }
  return resetController;
}

export function useAppRuntime(): AppRuntime {
  const runtime = useContext(AppRuntimeContext);
  if (!runtime) {
    throw new Error('Member React runtime is missing');
  }
  return runtime;
}
